import axios from "axios";

/**
 * Analyzes an uploaded image and returns a topic/description
 * @param {File} file - The image file to analyze
 * @param {Function} setTopicLoading - Function to set loading state
 * @param {Function} setImageGeneratedTopic - Function to set the generated topic
 * @param {Function} setEditedTopic - Function to set the edited topic
 * @param {Function} enqueueSnackbar - Function to show notifications
 * @returns {Promise<string>} The analysis text from the image
 */
export const handleImageUpload = async (
  file,
  setTopicLoading,
  setImageGeneratedTopic,
  setEditedTopic,
  enqueueSnackbar
) => {
  if (!file) return;

  setTopicLoading(true);

  try {
    // Read the file as base64
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onloadend = async () => {
        const base64Data = reader.result; // Full data URL including prefix

        try {
          const token = localStorage.getItem("token");
          // Call our backend API for image analysis
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/ai/image-analysis`,
            {
              image: {
                mimetype: file.type,
                data: base64Data,
              },
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          // Extract the analysis from the response
          const analysisText = response.data.analysis;

          if (analysisText) {
            // Set the analysis as the topic in the image section only
            setImageGeneratedTopic(analysisText); // Update the new state
            setEditedTopic(analysisText); // For local editing within the image tab

            enqueueSnackbar("Topic generated from image!", {
              variant: "success",
            });
            resolve(analysisText);
          } else {
            throw new Error("Could not generate topic from image");
          }
        } catch (error) {
          console.error("Error analyzing image with API:", error);

          // Display a more helpful error message
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.details ||
            error.message ||
            "Failed to analyze image";

          enqueueSnackbar(`Error: ${errorMessage}`, { variant: "error" });
          reject(error);
        } finally {
          setTopicLoading(false);
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
        enqueueSnackbar("Error reading file. Please try again.", {
          variant: "error",
        });
        setTopicLoading(false);
        reject(new Error("Error reading file"));
      };

      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error("File upload error:", error);
    enqueueSnackbar("File upload error. Please try again.", {
      variant: "error",
    });
    setTopicLoading(false);
    throw error;
  }
};
